import {
  toWSDate,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPanel,
  WSSectionToolbar,
  WSTabs,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayerPayeeStatus } from "@wingspanhq/payments/dist/interfaces/payerPayee";
import { PayerPayeeEngagementStatus } from "@wingspanhq/payments/dist/interfaces/payerPayeeEngagement";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import React from "react";
import { RouteComponentProps } from "react-router-dom";
import { useEngagementQuery } from "../../../../../query/engagements/queries/useEngagementQuery";
import { useFeatureFlags } from "../../../../../query/hooks/useFeatureFlags";
import { useQueryPayee } from "../../../../../query/payee/queries/useQueryPayee";
import { usePayeeEngagementByPayeeIdQuery } from "../../../../../query/payeeEngagements/queries/usePayeeEngagementByPayeeIdQuery";
import { useCollaboratorGroupsQuery } from "../../../../../query/payments/queries";
import { useQueryWorkDefinitions } from "../../../../../query/workDefinition/queries/useQueryWorkDefinitions";
import { WSQueries } from "../../../../../query/WSQuery";
import { FieldView } from "../../../../../shared/components/FieldView/FieldView";
import { ResourceHistory } from "../../../../../shared/components/ResourceHistory";
import { useEngagementDefaultActions } from "../../../../Engagements/hooks/useEngagementDefaultActions";
import { PayeeIntegrationWidget } from "../../../../Payers/components/IntegrationsSection/PayeeIntegrationWidget";
import { RateCardViewWidget } from "../../../../RateCards";
import { RequirementsTable } from "../../../../Requirements/components/RequirementsTable";
import { selectorRequirementsFromEngagement } from "../../../../Requirements/selectors/selectorRequirementsFromEngagement";
import { RequirementContextType } from "../../../../Requirements/types";
import { sortRequirementsByPriority } from "../../../../Requirements/utils";
import { TableWorkDefinitions } from "../../../../WorkDefinitions";
import { WidgetWorkLogsPayerView } from "../../../../WorkLogs/components/WidgetWorkLogsPayerView";
import { usePayeeEngagementHistoryResources } from "../../../hooks/usePayeeEngagementHistoryResources";
import { selectorPayeeEngagementNameTmp } from "../../../selectors/selectorPayeeEngagementsTmp";
import { selectorPayerPayeeEngagementName } from "../../../selectors/selectorPayerPayeeEngagementName";
import {
  buildEngagementHistoryPath,
  PAYEES_ROOT_PATH,
  useGoToPayeeDetails
} from "../../../utils";

type EngagementDetailsSidebarProps = RouteComponentProps<{
  engagementId: string;
  payeeId: string;
}>;

export const RoutePayeeEngagementDetails: React.FC<
  EngagementDetailsSidebarProps
> = ({
  match: {
    params: { engagementId, payeeId }
  },
  history
}) => {
  const queryFeatureFlags = useFeatureFlags();
  const queryPayee = useQueryPayee(payeeId);
  const queryEngagement = usePayeeEngagementByPayeeIdQuery(
    payeeId,
    engagementId
  );
  const queryGroups = useCollaboratorGroupsQuery();
  const fetchDefaultActions = useEngagementDefaultActions();
  const isEngagementActive =
    queryEngagement.data?.status !== PayerPayeeEngagementStatus.Inactive;

  const goToPayeeDetails = useGoToPayeeDetails();

  const historyResources = usePayeeEngagementHistoryResources(engagementId);
  const featureFlags = useFeatureFlags();

  // Fetch the engagement type to get work definitions
  const engagementTypeId = queryEngagement.data?.engagementId;
  const queryEngagementType = useEngagementQuery(engagementTypeId!, {
    enabled: !!engagementTypeId
  });

  // Extract work definition IDs from engagement type (once loaded)
  const workDefinitionIds =
    queryEngagementType.data?.workDefinitions?.map(wd => wd.workDefinitionId) ||
    [];

  // Fetch work definitions filtered by the engagement's attached IDs
  const queryWorkDefinitions = useQueryWorkDefinitions(
    { "filter[workDefinitionId][in]": workDefinitionIds },
    { enabled: workDefinitionIds.length > 0 }
  );
  const workDefinitions = queryWorkDefinitions.data || [];
  const hasWorkDefinitions = workDefinitions.length > 0;

  return (
    <WSPage>
      <WSQueries queries={{ queryEngagement, queryPayee, queryGroups }}>
        {({ queryEngagementData, queryPayeeData, queryGroupsData }) => {
          const requirements = sortRequirementsByPriority(
            selectorRequirementsFromEngagement(queryEngagementData)
          );

          const engagementName = isEngagementActive
            ? selectorPayerPayeeEngagementName(queryEngagementData, true)
            : selectorPayeeEngagementNameTmp(
                queryEngagementData,
                queryGroupsData,
                true
              );

          const isInactive =
            queryEngagementData.status === PayerPayeeEngagementStatus.Inactive;

          const groupNames = queryGroupsData
            .filter(group =>
              queryEngagementData.collaboratorGroupIds?.includes(
                group.collaboratorGroupId
              )
            )
            .map(group => group.name);

          const names = wsName({
            user: {
              email: queryPayeeData.user?.email as string,
              profile: queryPayeeData.user?.profile
            },
            member: queryPayeeData.member
          });

          const requirementsTable = (
            <RequirementsTable
              requirements={requirements}
              contextType={RequirementContextType.Payee}
              onViewDetails={async (requirement, additionalData) => {
                if ("requirementId" in requirement) {
                  history.push(
                    `/member/payees/${payeeId}/requirement/${requirement.requirementId}?engagementId=${queryEngagementData.payerPayeeEngagementId}`
                  );
                }
              }}
            />
          );

          return (
            <WSList>
              <WSPageToolbar
                title={engagementName}
                titleAfter={
                  <WSText.ParagraphXs color="gray500">
                    Last modified{" "}
                    {toWSDate(queryEngagementData.updatedAt).formattedValue}
                  </WSText.ParagraphXs>
                }
                fetchActions={
                  queryPayeeData.payerOwnedData.status ===
                  PayerPayeeStatus.Active
                    ? () =>
                        fetchDefaultActions({
                          engagement: queryEngagementData,
                          payee: queryPayeeData
                        })
                    : undefined
                }
                titlePill={
                  queryEngagementData.status ===
                  PayerPayeeEngagementStatus.Inactive
                    ? {
                        theme: "neutral",
                        text: "Archived",
                        icon: false
                      }
                    : undefined
                }
                breadcrumbs={[
                  {
                    label: "Contractors",
                    onClick: () => {
                      history.push(PAYEES_ROOT_PATH);
                    }
                  },
                  {
                    label: names.getResolvedName(),
                    onClick: () => {
                      goToPayeeDetails(queryPayeeData.payeeId);
                    }
                  },
                  {
                    label: engagementName || "Engagement"
                  }
                ]}
              />
              <WSGrid>
                <WSGrid.Item span={{ m: "12", l: "8" }}>
                  <WSList gap="L">
                    <WSElement>
                      <WSSectionToolbar title="Engagement information" />
                      <WSPanel>
                        <WSFlexBox gap="XL">
                          <FieldView
                            label="Engagement name"
                            value={engagementName}
                          />
                          {/*
                   <FieldView
                      label="Type"
                      value={getEngagementLabelByType(
                        queryEngagementData.type,
                        {
                          descriptive: true
                        }
                      )}
                    />
                    */}
                        </WSFlexBox>
                        {groupNames.length ? (
                          <FieldView
                            mt="XL"
                            label={`Group${groupNames.length > 1 ? "s" : ""}`}
                            value={groupNames.join(", ")}
                          />
                        ) : null}
                        {/* <FieldView
                  mt="XL"
                  label="Description"
                  value={"TODO:description"}
                /> */}
                      </WSPanel>
                    </WSElement>
                    {featureFlags.data?.displayWorkDefinitions &&
                    hasWorkDefinitions ? (
                      <WSTabs
                        tabs={[
                          {
                            label: "Work definitions",
                            render: () => (
                              <TableWorkDefinitions
                                workDefinitions={workDefinitions}
                              />
                            )
                          },
                          {
                            label: "Requirements",
                            render: () => requirementsTable
                          }
                        ]}
                      />
                    ) : (
                      <WSElement>
                        <WSElement>
                          <WSSectionToolbar title="Engagement requirements" />

                          {requirementsTable}
                        </WSElement>
                      </WSElement>
                    )}
                  </WSList>
                </WSGrid.Item>
                <WSGrid.Item span={{ m: "12", l: "4" }}>
                  <WSList gap="L">
                    {featureFlags.data?.rateCardOverride && (
                      <RateCardViewWidget
                        payeeId={payeeId}
                        payeeEngagementId={
                          queryEngagementData.payerPayeeEngagementId
                        }
                        context="contractor-assignment"
                        canCustomize={true}
                      />
                    )}
                    <PayeeIntegrationWidget
                      isInactive={isInactive}
                      payeeId={queryPayeeData.payeeId}
                      engagementId={queryEngagementData.payerPayeeEngagementId}
                      vendorId={
                        queryEngagement.data?.payerOwnedData.integration
                          ?.quickbooks?.vendorId
                      }
                      expenseAccountId={
                        queryEngagement.data?.payerOwnedData.integration
                          ?.quickbooks?.expenseAccountId
                      }
                    />

                    {queryFeatureFlags.data?.workLogsList && (
                      <WidgetWorkLogsPayerView
                        filter={{
                          payeeId: queryPayeeData.payeeId,
                          engagementId: queryEngagementData.engagementId
                        }}
                      />
                    )}
                    <ResourceHistory
                      resources={historyResources}
                      onViewAll={() => {
                        history.push(
                          buildEngagementHistoryPath(payeeId, engagementId)
                        );
                      }}
                    />
                  </WSList>
                </WSGrid.Item>
              </WSGrid>

              {/*
                <WSPanel pt="S">
                  <CollaboratorsGroupsForm
                    isInactive={isInactive}
                    collaboratorId={queryEngagementData.payerPayeeEngagementId}
                    collaboratorGroupIds={
                      queryEngagementData.collaboratorGroupIds
                    }
                  />
                </WSPanel>
                */}
            </WSList>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
